import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { resolveCueTransportBusy } from '@/helpers/resolve-cue-transport-busy.helper';
import { useCueTransportBusyStore } from '@/state/player/cue-transport-busy.store';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import { GET_LIVE_CONTEXT_STATE_QUERY_KEY } from '@/state/queries/player/use-get-live-context-state';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';

/**
 * Latch transport/Next-Play busy on click; clear on cue on_air progress, error, or HTTP settle.
 * Shared per entityRef so Next preview and the prev/next transport bar show the same spinner.
 * Watchdog only refetches — does not re-enable Play.
 */
export function usePerformTransportBusy(entityRef: string | undefined, cueState: FlowCueStateV1 | undefined) {
  const queryClient = useQueryClient();
  const latch = useCueTransportBusyStore((s) => (entityRef ? (s.latchByEntityRef[entityRef] ?? null) : null));
  const mutationFailed = useCueTransportBusyStore((s) =>
    entityRef ? Boolean(s.mutationFailedByEntityRef[entityRef]) : false,
  );
  const mutationSettled = useCueTransportBusyStore((s) =>
    entityRef ? Boolean(s.mutationSettledByEntityRef[entityRef]) : false,
  );
  const tryLatchStore = useCueTransportBusyStore((s) => s.tryLatch);
  const markFailedStore = useCueTransportBusyStore((s) => s.markMutationFailed);
  const markSettledStore = useCueTransportBusyStore((s) => s.markMutationSettled);
  const consumeWatchdogRefetch = useCueTransportBusyStore((s) => s.consumeWatchdogRefetch);
  const retainObserver = useCueTransportBusyStore((s) => s.retainObserver);
  const releaseObserver = useCueTransportBusyStore((s) => s.releaseObserver);
  const clearLatch = useCueTransportBusyStore((s) => s.clearLatch);

  useEffect(() => {
    if (!entityRef) {
      return;
    }
    retainObserver(entityRef);
    return () => {
      releaseObserver(entityRef);
    };
  }, [entityRef, releaseObserver, retainObserver]);

  const roomKey = entityRef ? entityRoomKeyFromEntityRef(entityRef) : undefined;
  const seq = usePerformControlWsStore((s) => (roomKey ? (s.lastSeqByRoom[roomKey] ?? 0) : 0));

  const pointer = cueState?.pointer;
  const currentPhase = cueState
    ? Object.values(cueState.items ?? {}).find((entry) => entry.isCurrent)?.phase
    : undefined;

  const resolved = resolveCueTransportBusy({
    latch,
    pointer,
    currentPhase,
    seq,
    mutationFailed,
    mutationSettled,
  });

  useEffect(() => {
    if (!entityRef || !resolved.clearLatch) {
      return;
    }
    clearLatch(entityRef);
  }, [clearLatch, entityRef, resolved.clearLatch]);

  useEffect(() => {
    if (!resolved.watchdogFired || !entityRef) {
      return;
    }
    if (!consumeWatchdogRefetch(entityRef)) {
      return;
    }
    void queryClient.refetchQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
  }, [resolved.watchdogFired, entityRef, queryClient, consumeWatchdogRefetch]);

  useEffect(() => {
    if (!latch?.active || !entityRef) {
      return;
    }
    const id = window.setInterval(() => {
      useCueTransportBusyStore.setState((state) => ({
        latchByEntityRef: { ...state.latchByEntityRef },
      }));
    }, 250);
    return () => window.clearInterval(id);
  }, [entityRef, latch?.active]);

  const tryLatchBusy = useCallback((): boolean => {
    if (!entityRef) {
      return true;
    }
    const room = entityRoomKeyFromEntityRef(entityRef);
    const currentSeq = room ? (usePerformControlWsStore.getState().lastSeqByRoom[room] ?? 0) : 0;
    return tryLatchStore(entityRef, cueState?.pointer ?? 0, currentSeq);
  }, [cueState?.pointer, entityRef, tryLatchStore]);

  const latchBusy = useCallback(() => {
    void tryLatchBusy();
  }, [tryLatchBusy]);

  const markMutationFailed = useCallback(() => {
    if (entityRef) {
      markFailedStore(entityRef);
    }
  }, [entityRef, markFailedStore]);

  const markMutationSettled = useCallback(() => {
    if (entityRef) {
      markSettledStore(entityRef);
    }
  }, [entityRef, markSettledStore]);

  return {
    isBusy: resolved.busy,
    tryLatchBusy,
    latchBusy,
    markMutationFailed,
    markMutationSettled,
  };
}
